/* eslint-disable no-unused-vars */
import { useState, useContext, useEffect, useRef } from "react";
import { AdminContext } from "../../../context/AdminContext";
import { ClipboardList, Filter, ChevronDown, RefreshCw } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import Pagination from "../../../components/Pagination";

const actionLabels = {
  blog_created: {
    label: "Blog Created",
    color: "bg-green-600 text-white dark:bg-green-700 dark:text-white",
  },
  blog_updated: {
    label: "Blog Updated",
    color: "bg-blue-600 text-white dark:bg-blue-700 dark:text-white",
  },
  blog_deleted: {
    label: "Blog Deleted",
    color: "bg-red-600 text-white dark:bg-red-700 dark:text-white",
  },
  blog_published: {
    label: "Blog Published",
    color: "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white",
  },
  blog_unpublished: {
    label: "Blog Unpublished",
    color: "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white",
  },
  blog_scheduled: {
    label: "Blog Scheduled",
    color: "bg-purple-600 text-white dark:bg-purple-700 dark:text-white",
  },
  blog_bulk_published: {
    label: "Bulk Published",
    color: "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white",
  },
  blog_bulk_deleted: {
    label: "Bulk Deleted",
    color: "bg-red-600 text-white dark:bg-red-700 dark:text-white",
  },
  category_created: {
    label: "Category Created",
    color: "bg-indigo-600 text-white dark:bg-indigo-700 dark:text-white",
  },
  category_updated: {
    label: "Category Updated",
    color: "bg-cyan-600 text-white dark:bg-cyan-700 dark:text-white",
  },
  category_deleted: {
    label: "Category Deleted",
    color: "bg-orange-600 text-white dark:bg-orange-700 dark:text-white",
  },
};

const ActivityLogs = () => {
  const { activityLogs, activityLogsPagination, fetchActivityLogs } =
    useContext(AdminContext);
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const actionDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(event.target)
      ) {
        setShowActionDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filterAction]);

  const loadLogs = async () => {
    setLoading(true);
    await fetchActivityLogs({
      page: activityLogsPagination.page,
      limit: activityLogsPagination.limit,
      action: filterAction || undefined,
    });
    setLoading(false);
  };

  const handlePageChange = (page) => {
    fetchActivityLogs({
      page,
      limit: activityLogsPagination.limit,
      action: filterAction || undefined,
    });
  };

  const handleItemsPerPageChange = (limit) => {
    fetchActivityLogs({ page: 1, limit, action: filterAction || undefined });
  };

  const handleResetFilters = () => {
    setFilterAction("");
    fetchActivityLogs({
      page: 1,
      limit: activityLogsPagination.limit,
    });
  };

  const getActionLabel = () => {
    if (!filterAction) return "All Actions";
    return actionLabels[filterAction]?.label || filterAction;
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text flex items-center gap-3">
          <ClipboardList className="text-primary" /> Activity Logs
        </h1>
        <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm">
          Track all admin actions on blogs and categories.
        </p>
      </div>

      {/* Filter */}
      <div className="bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex gap-2">
        {/* Action Filter Dropdown */}
        <div className="relative flex-1 max-w-sm" ref={actionDropdownRef}>
          <button
            onClick={() => setShowActionDropdown(!showActionDropdown)}
            className="w-full flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors"
          >
            <Filter size={16} />
            <span className="flex-1 text-left">{getActionLabel()}</span>
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showActionDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showActionDropdown && (
            <div className="absolute left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200 max-h-80 overflow-y-auto">
              <button
                onClick={() => {
                  setFilterAction("");
                  setShowActionDropdown(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  !filterAction
                    ? "bg-primary text-white dark:bg-primary"
                    : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                }`}
              >
                All Actions
              </button>
              {Object.entries(actionLabels).map(([key, { label }]) => (
                <button
                  key={key}
                  onClick={() => {
                    setFilterAction(key);
                    setShowActionDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    filterAction === key
                      ? "bg-primary text-white dark:bg-primary"
                      : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reset Button */}
        <button
          onClick={handleResetFilters}
          className="flex items-center justify-center px-4 py-3 rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
          title="Reset Filters"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Logs Table */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-gray-500 dark:text-dark-muted mt-4 font-medium">
              Loading...
            </p>
          </div>
        ) : activityLogs.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text dark:text-dark-text mb-2">
              No activity logs
            </h3>
            <p className="text-gray-500 dark:text-dark-muted text-sm">
              Actions on blogs and categories will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-dark-border">
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted">
                    Admin
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted">
                    Action
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted hidden md:table-cell">
                    Details
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((log) => {
                  const actionInfo = actionLabels[log.action] || {
                    label: log.action,
                    color: "bg-gray-600 text-white",
                  };
                  return (
                    <tr
                      key={log.id}
                      className="border-b border-gray-50 dark:border-dark-border/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {log.admin?.image ? (
                            <img
                              src={log.admin.image}
                              alt=""
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                              {log.admin?.name?.charAt(0) || "A"}
                            </div>
                          )}
                          <span className="font-medium text-text dark:text-dark-text text-sm">
                            {log.admin?.name || "Unknown"}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${actionInfo.color}`}
                        >
                          {actionInfo.label}
                        </span>
                      </td>
                      <td className="p-4 hidden md:table-cell text-gray-500 dark:text-dark-muted text-xs max-w-[200px] truncate">
                        {log.details?.title ||
                          log.details?.name ||
                          (log.details?.count
                            ? `${log.details.count} items`
                            : "—")}
                      </td>
                      <td className="p-4 text-gray-500 dark:text-dark-muted text-xs">
                        {new Date(log.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}{" "}
                        <br />
                        {new Date(log.createdAt).toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {activityLogsPagination.totalPages > 0 && (
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30 p-2 mt-6">
          <Pagination
            currentPage={activityLogsPagination.page}
            totalPages={activityLogsPagination.totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={activityLogsPagination.limit}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={activityLogsPagination.total}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default ActivityLogs;
